export const editablePaiReportForm = {
  pretext: {
    en: `**Statement on principal adverse impacts of investment decisions on sustainability factors**
<br>
<br>
All information in this view is required for the PAI reporting. Therefore, it is essential that you carefully review that all the information and data provided is accurate and complete.

<strong style="color: var(--v-primary-base)">Please carefully read the following introduction on how to use the template.</strong>

**Using the Templates:**
Our system generated texts automatically based on data you’ve provided in the Fund Setup and your portfolio companies provided as part of the PAI reporting. These texts can be easily transferred to the input field by clicking on the “Replace” button.

**Reviewing and Editing the Template:**
It’s crucial to carefully review the questions and the automatically generated sentences, and if needed, add any data that is missing to make sure all information has been included.

**Personalizing the Responses:**
The templates serve as guidelines. You have the flexibility to use the provided texts in their entirety, write your own response, or combine the two approaches to complete the questionnaire. Most of the fields can be edited to provide a more accurate or detailed response. However, please note that some fields are locked and cannot be edited. Regardless, a review is always necessary to ensure accuracy and completeness.

Once ready, your “PAI statement” should be published on your website in the SFDR section “Sustainability-related disclosures” as a subsection titled: ‘Statement on principal adverse impacts of investment decisions on sustainability factors’.`,
    de: `**Erklärung zu den wichtigsten nachteiligen Auswirkungen von Investitionsentscheidungen auf Nachhaltigkeitsfaktoren**
<br>
<br>
Alle Informationen in dieser Ansicht werden für das PAI-Reporting benötigt. Daher ist es von entscheidender Bedeutung, dass Sie alle bereitgestellten Informationen und Daten sorgfältig prüfen, um deren Richtigkeit und Vollständigkeit sicherzustellen.

<strong style="color: var(--v-primary-base)">Bitte lesen Sie die folgende Einführung zur Verwendung der Vorlage sorgfältig durch.</strong>

**Verwendung der Vorlagen:**
Unsere Systemtexte werden automatisch basierend auf den von Ihnen im Fonds-Setup bereitgestellten Daten und den Informationen Ihrer Portfoliounternehmen im Rahmen des PAI-Reportings generiert. Die Texte können Sie ganz einfach durch Klicken auf die Schaltfläche „Ersetzen“ in das Eingabefeld übernehmen.

**Überprüfung und Bearbeitung der Vorlage:**
Es ist entscheidend, die Fragen und die automatisch generierten Sätze sorgfältig zu überprüfen und, falls erforderlich, fehlende Daten hinzuzufügen, um sicherzustellen, dass alle Informationen enthalten sind.

**Personalisierung der Antworten:**
Die Vorlagen dienen als Leitfaden. Sie können die bereitgestellten Texte vollständig übernehmen, Ihre eigenen Formulierungen verwenden oder beide Ansätze kombinieren, um den Fragebogen zu vervollständigen. Die meisten Felder sind bearbeitbar und ermöglichen genauere oder detailliertere Antworten. Bitte beachten Sie, dass einige Felder gesperrt sind und nicht bearbeitet werden können. Eine Überprüfung ist jedoch immer erforderlich, um Richtigkeit und Vollständigkeit zu gewährleisten.

Sobald Sie fertig sind, sollten Sie Ihre „PAI-Erklärung“ auf Ihrer Website im SFDR-Abschnitt „Nachhaltigkeitsbezogene Offenlegungen“ als Unterabschnitt mit dem Titel: „Erklärung zu den wichtigsten nachteiligen Auswirkungen von Investitionsentscheidungen auf Nachhaltigkeitsfaktoren“ veröffentlichen.`,
  },

  fundDetails: {
    fundName: {
      en: 'What is the name of the fund?',
      de: 'Wie lautet der Name des Fonds?',
    },
    fundNameTooltip: {
      en: 'This field is populated from the Fund Setup. To make changes, update the Fund Setup module.',
      de: 'Dieses Feld wird aus dem Fonds-Setup übernommen. Änderungen nehmen Sie im Modul „Fonds-Setup“ vor.',
    },
    legalName: {
      en: 'What is the name of the entity?',
      de: 'Wie lautet der Name der juristischen Person?',
    },
    legalNameTooltip: {
      en: 'This field is populated from the Fund Setup. To make changes, update the Fund Setup module.',
      de: 'Dieses Feld wird aus dem Fonds-Setup übernommen. Änderungen nehmen Sie im Modul „Fonds-Setup“ vor.',
    },
    legalEntityIdentifier: {
      en: 'What is the legal entity identifier (LEI code)?',
      de: 'Wie lautet der Legal Entity Identifier (LEI-Code)?',
    },
    legalEntityIdentifierTooltip: {
      en: 'This field is populated from the Fund Setup. To make changes, update the Fund Setup module.',
      de: 'Dieses Feld wird aus dem Fonds-Setup übernommen. Änderungen nehmen Sie im Modul „Fonds-Setup“ vor.',
    },
  },

  title: {
    article8: {
      en: 'Environmental and/or social characteristics',
      de: 'Ökologische und/oder soziale Merkmale',
    },
    article8Tooltip: {
      en: `This field is populated automatically, using the details you've provided in the Fund Setting Module. Modifications cannot be made directly in this field.`,
      de: `Dieses Feld wird automatisch basierend auf den im Modul „Fund Setting“ bereitgestellten Angaben ausgefüllt. Änderungen können nicht direkt in diesem Feld vorgenommen werden.`,
    },
    article9: {
      en: 'Sustainable Investment Objective',
      de: 'Nachhaltiges Anlageziel',
    },
    article9Tooltip: {
      en: '',
      de: '',
    },
  },

  questions: {
    sustainableObjectiveTree: {
      title: {
        en: 'Did this financial product have a sustainable investment objective?',
        de: 'Verfolgte dieses Finanzprodukt ein nachhaltiges Anlageziel?',
      },
      tooltip: {
        en: 'Sustainable investment means an investment in an economic activity that contributes to an environmental or social objective, provided that the investment is compliant with the “do no significant harm” principle (for which disclosure of the PAI indicators is required), and that the investee companies follow good governance practice',
        de: 'Eine nachhaltige Investition ist eine Investition in eine wirtschaftliche Tätigkeit, die zu einem Umwelt- oder Sozialziel beiträgt, sofern die Investition mit dem „Do-No-Significant-Harm“-Prinzip (für das eine Offenlegung der PAI-Indikatoren erforderlich ist) vereinbar ist und die Portfoliounternehmen eine gute Unternehmensführung praktizieren.',
      },
    },
    sustainabilityIndicatorsPerformanceDescription: {
      title: {
        en: 'How did the sustainability indicators perform?',
        de: 'Wie haben sich die Nachhaltigkeitsindikatoren entwickelt?',
      },
      subtitle: {
        article8: {
          en: 'List the environmental and/or social characteristics promoted by the financial product. For the financial products referred to in Article 6, first paragraph, of Regulation (EU) 2020/852, in respect of sustainable investments with environmental objectives, list the environmental objectives set out in Article 9 of that Regulation to which the sustainable investment underlying the financial product contributed. For financial products that made sustainable investments with social objectives, list the social objectives',
          de: 'Listen Sie die durch das Finanzprodukt geförderten Ökologische und/oder soziale Merkmale auf. Für die in Artikel 6 Absatz 1 der Verordnung (EU) 2020/852 genannten Finanzprodukte, die nachhaltige Investitionen mit Umweltzielen umfassen, nennen Sie die in Artikel 9 dieser Verordnung aufgeführten Umweltziele, zu denen die nachhaltige Investition des Finanzprodukts beigetragen hat. Für Finanzprodukte, die nachhaltige Investitionen mit sozialen Zielen getätigt haben, listen Sie die entsprechenden sozialen Ziele auf.',
        },
        article9: {
          en: 'List the sustainable investment objective of this financial product, and describe how the sustainable investments contributed to the sustainable investment objective. For the financial products referred to in Article 5, first paragraph, of Regulation (EU) 2020/852, in respect of sustainable investments with environmental objectives, indicate to which environmental objectives set out in Article 9 of Regulation (EU) 2020/852 to the investment underlying the financial product contributed to. For the financial products referred to in Article 9(3) of Regulation (EU) 2019/2088, indicate how the objective of a reduction in carbon emissions was aligned with the Paris Agreement',
          de: 'Führen Sie das nachhaltige Anlageziel dieses Finanzprodukts auf und beschreiben Sie, wie die nachhaltigen Investitionen zu diesem Ziel beigetragen haben. Für die in Artikel 5 Absatz 1 der Verordnung (EU) 2020/852 genannten Finanzprodukte, die nachhaltige Investitionen mit Umweltzielen umfassen, geben Sie an, zu welchen Umweltzielen gemäß Artikel 9 der Verordnung (EU) 2020/852 die zugrunde liegende Investition des Finanzprodukts beigetragen hat. Für die in Artikel 9(3) der Verordnung (EU) 2019/2088 genannten Finanzprodukte geben Sie an, wie das Ziel der Verringerung von Kohlenstoffemissionen mit dem Übereinkommen von Paris in Einklang steht.',
        },
      },
      tooltip: {
        en: 'Sustainability indicators measure how the environmental or social characteristics promoted by the fund are attained.',
        de: 'Nachhaltigkeitsindikatoren messen, inwieweit die durch den Fonds geförderten Umwelt- oder Sozialmerkmale erreicht werden.',
      },
    },
    sustainabilityIndicatorsPerformanceDescriptionComparedToPrevious: {
      title: {
        en: 'And compared to previous periods?',
        de: 'Und im Vergleich zu den vorherigen Zeiträumen?',
      },
      subtitle: {
        en: 'include for financial products where at least one previous periodic report was provided',
        de: 'Geben Sie diese Information an, wenn für das Finanzprodukt bereits mindestens ein vorheriger periodischer Bericht vorgelegt wurde',
      },
    },
    accountOnPais: {
      title: {
        en: 'How were the indicators for adverse impacts on sustainability factors taken into account?',
        de: 'Wie wurden die Indikatoren für nachteilige Auswirkungen auf Nachhaltigkeitsfaktoren berücksichtigt?',
      },
      tooltip: {
        en: 'Principal adverse impacts are the most significant negative impacts of investment decisions on sustainability factors relating to environmental, social and employee matters, respect for human right, anti-corruption and anti-bribery matters. For a fund to be considered Article 8+ or Article 9, the information on the Principal Adverse Impacts (PAIs) of the underlying investments should be provided.',
        de: 'Die wichtigsten nachteiligen Auswirkungen sind die bedeutendsten negativen Folgen von Anlageentscheidungen auf Nachhaltigkeitsfaktoren wie Umwelt-, Sozial- und Arbeitnehmerbelange, die Achtung der Menschenrechte sowie Korruptions- und Bestechungsbekämpfung. Damit ein Fonds als Artikel 8+ oder Artikel 9 eingestuft wird, sollten Informationen zu den Principal Adverse Impacts (PAIs) der zugrunde liegenden Investitionen bereitgestellt werden.',
      },
    },
    howDidProductConsiderPais: {
      title: {
        en: 'Description of the principal adverse impacts on sustainability factors',
        de: 'Beschreibung der wichtigsten nachteiligen Auswirkungen auf Nachhaltigkeitsfaktoren',
      },
    },
    paisTable: {
      title: {
        en: 'For each indicator enter an explanation and actions planned/targets set for the next reference period.',
        de: 'Geben Sie für jeden Indikator eine Erklärung ab und nennen Sie die geplanten Maßnahmen bzw. Ziele für den nächsten Berichtszeitraum.',
      },
      tooltip: {
        en: `The percentage alongside each indicator value indicates the <strong>coverage</strong>. If one out of 11 companies assigned to this fund have completed that indicator, the coverage would be 9%.
<br>
<br>
<strong>Please note</strong> that where information relating to any of the indicators used is not readily available, you shall provide details of the best efforts used to obtain the information either directly from investee companies, or by carrying out additional research, cooperating with third party data providers or external experts or making reasonable assumptions. This should be done in the section ‘Description of policies to identify and prioritize principal adverse impacts on sustainability factors’, according to <strong>Article 6(2) of COMMISSION DELEGATED REGULATION (EU) 2022/1288.</strong>`,
        de: `Der Prozentwert neben jedem Indikatorwert gibt die <strong>Abdeckung</strong> an. Wenn beispielsweise eine von 11 dem Fonds zugeordneten Gesellschaften diesen Indikator ausgefüllt hat, beträgt die Abdeckung 9%.
<br>
<br>
<strong>Bitte beachten Sie</strong>, dass wenn Informationen zu einem der verwendeten Indikatoren nicht ohne Weiteres verfügbar sind, Sie im Abschnitt „Beschreibung der Richtlinien zur Identifizierung und Priorisierung der wichtigsten nachteiligen Auswirkungen auf Nachhaltigkeitsfaktoren“ (gemäß <strong>Artikel 6(2) der Delegierten Verordnung (EU) 2022/1288</strong>) angeben müssen, welche Maßnahmen Sie unternommen haben, um die Informationen direkt von den Portfoliounternehmen zu erhalten, zusätzliche Recherchen durchzuführen, mit Drittanbietern von Daten oder externen Experten zusammenzuarbeiten oder angemessene Annahmen zu treffen.`,
      },
    },
    considerationOfPaisOnSustainabilityDescription: {
      title: {
        en: 'Description of policies to identify and prioritise principal adverse impacts on sustainability factors',
        de: 'Beschreibung der Richtlinien zur Identifizierung und Priorisierung der wichtigsten nachteiligen Auswirkungen auf Nachhaltigkeitsfaktoren',
      },
      subtitle: {
        en: 'Information referred to in Article 7.',
        de: 'Informationen gemäß Artikel 7.',
      },
      tooltip: {
        en: `Where information relating to any PAI indicator used is not readily available, please provide details of the best efforts used to obtain the information either directly from investee companies, or by carrying out additional research, cooperating with third party data providers or external experts or making reasonable assumptions and describe them in this field.
<br>
<br>
Key elements of description based on Article 7:
<br>
<ul>
  <li>Policy approval date by the governing body.</li>
  <li>Allocation of responsibility for policy implementation within organizational strategies and procedures.</li>
  <li>Methodologies for selecting indicators, and identifying and assessing principal adverse impacts, taking into account their probability, severity, and potentially irremediable nature.</li>
  <li>Any associated margin of error within the methodologies and an explanation for it.</li>
  <li>Data sources used.</li>
</ul>`,
        de: `Sollten Informationen zu einem verwendeten PAI-Indikator nicht leicht verfügbar sein, geben Sie bitte Details zu den von Ihnen unternommenen Maßnahmen an, um diese Informationen direkt von den Portfoliounternehmen zu erhalten, zusätzliche Recherchen durchzuführen, mit Drittanbietern von Daten oder externen Experten zusammenzuarbeiten oder angemessene Annahmen zu treffen. Beschreiben Sie diese in diesem Abschnitt.
<br>
<br>
Zentrale Elemente gemäß Artikel 7:
<br>
<ul>
  <li>Datum der Genehmigung der Richtlinie durch das leitende Gremium.</li>
  <li>Zuweisung der Verantwortung für die Umsetzung der Richtlinie im Rahmen organisatorischer Strategien und Verfahren.</li>
  <li>Methoden zur Auswahl von Indikatoren sowie zur Identifizierung und Bewertung der wichtigsten nachteiligen Auswirkungen unter Berücksichtigung ihrer Wahrscheinlichkeit, Schwere und potenziellen Unumkehrbarkeit.</li>
  <li>Mögliche Fehlermargen in den Methoden und eine entsprechende Erläuterung.</li>
  <li>Verwendete Datenquellen.</li>
</ul>`,
      },
    },
    considerationOfPaisOnSustainabilityEngagementPolicies: {
      title: {
        en: 'Engagement policies',
        de: 'Engagement-Richtlinien',
      },
      subtitle: {
        en: 'Information referred to in Article 8',
        de: 'Informationen gemäß Artikel 8',
      },
    },
    considerationOfPaisOnSustainabilityInternationalStandards: {
      title: {
        en: 'References to international standards',
        de: 'Verweise auf internationale Standards',
      },
      subtitle: {
        en: 'Information referred to in Article 9',
        de: 'Informationen gemäß Artikel 9',
      },
    },
    considerationOfPaisOnSustainabilityHistoricalComparison: {
      title: {
        en: 'Historical comparison',
        de: 'Historischer Vergleich',
      },
      subtitle: {
        en: 'Information referred to in Article 10',
        de: 'Informationen gemäß Artikel 10',
      },
      tooltip: {
        en: 'Please provide a summary of how the principal adverse impact (PAI) values have changed during the most recent reporting period compared to the previous one. If there were significant increases or decreases in any PAIs, explain the reasons behind these changes.',
        de: 'Bitte geben Sie eine Zusammenfassung der Änderungen der wichtigsten nachteiligen Auswirkungen (PAI) während des am neuesten veröffentlichten Berichtszeitraums im Vergleich zum vorherigen Berichtszeitraum an. Wenn es in einem oder mehreren PAIs wesentliche Zunahmen oder Abnahmen gegeben hat, erläutern Sie die Gründe für diese Änderungen.',
      },
    },
    summaryOfPais: {
      title: {
        en: 'Summary',
        de: 'Zusammenfassung',
      },
      tooltip: {
        en: `Please make sure that this field contains the following information:
1. the name of the entity to which the adverse sustainability impacts statement relates;
2. the fact that principal adverse impacts on sustainability factors are considered by the entity;
3. the reference period of the statement;
4. a summary of the principal adverse impacts.`,
        de: `Bitte stellen Sie sicher, dass dieses Feld die folgenden Informationen enthält:
1. den Namen der juristischen Person, auf die sich die Erklärung zu den nachteiligen Nachhaltigkeitsauswirkungen bezieht;
2. die Tatsache, dass das Unternehmen die wichtigsten nachteiligen Auswirkungen auf Nachhaltigkeitsfaktoren berücksichtigt;
3. den Berichtszeitraum der Erklärung;
4. eine Zusammenfassung der wichtigsten nachteiligen Auswirkungen.`,
      },
    },
  },
}
